import React, { useState, useEffect, useRef } from 'react';
import {
  MdCheck, MdShield,
  MdChatBubble, MdReceiptLong, MdCheckCircle, MdArrowForward, MdCalendarMonth,
  MdCreditCard, MdForum, MdFolderOpen, MdSavings, MdQrCode2,
  MdLink, MdAdd, MdAndroid, MdPhoneIphone
} from 'react-icons/md';
import { motion, useScroll, useTransform, useInView, useMotionValue, animate, AnimatePresence, useReducedMotion } from 'framer-motion';
import Layout from '../components/Layout';
import phoneLeft from '../assets/phone-left-opt.webp';

// --- Animation Variants & Components ---

// 1. Text Reveal Variant (Staggered words or lines)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20
    }
  },
};

// 2. CountUp Component
function CountUp({ to, duration = 1.5, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20px" });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (inView) {
      const controls = animate(0, to, {
        duration,
        ease: "easeOut",
        onUpdate: (value) => setDisplayValue(Math.round(value))
      });
      return controls.stop;
    }
  }, [inView, to, duration]);

  return <span ref={ref}>{displayValue}{suffix}</span>;
}

// 3. 3D Card Effect
function Card3D({ children, className }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [5, -5]); // Inverted for tilt
  const rotateY = useTransform(x, [-100, 100], [-5, 5]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(e.clientX - centerX);
    y.set(e.clientY - centerY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      className={className}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        perspective: 1000,
      }}
    >
      <motion.div
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        className="h-full w-full"
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

// Main Component
export default function ExactLandingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const prefersReducedMotion = useReducedMotion();

  // Parallax for Hero
  const { scrollY } = useScroll();
  const yHeroPhone = useTransform(scrollY, [0, 500], prefersReducedMotion ? [0, 0] : [0, 100]);
  const yHeroBadge1 = useTransform(scrollY, [0, 500], prefersReducedMotion ? [0, 0] : [0, -50]);
  const yHeroBadge2 = useTransform(scrollY, [0, 500], prefersReducedMotion ? [0, 0] : [0, -80]);

  return (
    <Layout>
      <div className="antialiased scroll-smooth overflow-x-hidden selection:bg-purple-600 selection:text-white bg-white text-gray-900">

        {/* The Header and Scroll Progress Bar are now handled in the Layout/SharedHeader component */}

        <section className="relative pt-16 pb-8 lg:pt-12 lg:pb-16 overflow-hidden bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

              {/* LEFT COLUMN: Text Content */}
              <motion.div
                className="order-2 lg:order-1"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="max-w-xl mx-auto lg:mx-0 text-center lg:text-left">

                  <div className="overflow-hidden mb-4 pb-2">
                    <motion.h1 variants={itemVariants} className="text-[40px] md:text-[56px] lg:text-[64px] font-semibold text-gray-900 leading-[1.1] tracking-tight" style={{ fontFamily: "'Swansea', sans-serif" }}>
                      The booking app <br /> that protects <br /> your income.
                    </motion.h1>
                  </div>

                  <motion.p variants={itemVariants} className="text-[16px] md:text-[18px] text-gray-600 leading-relaxed mb-4 md:mb-6 max-w-lg">
                    Automate deposits, eliminate no-shows, and manage your clients, all from one app. Built for independent beauty pros.
                  </motion.p>

                  <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-6 md:mb-8">
                    <motion.a
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://apps.apple.com/ca/app/uhyve/id6751551335"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto bg-purple-600 text-white px-8 py-4 rounded-full font-bold hover:bg-purple-700 shadow-xl flex items-center justify-center gap-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                      aria-label="Download HYVE for iOS"
                    >
                      <MdPhoneIphone className="text-xl" aria-hidden="true" />
                      <span>Download for iOS</span>
                    </motion.a>
                    <motion.a
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://play.google.com/store/apps/details?id=com.uhyve.app&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto border-2 border-gray-200 text-gray-900 px-8 py-4 rounded-full font-bold hover:border-purple-600 hover:text-purple-600 flex items-center justify-center gap-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 transition-colors"
                      aria-label="Download HYVE for Android"
                    >
                      <MdAndroid className="text-xl" aria-hidden="true" />
                      <span>Download for Android</span>
                    </motion.a>
                  </motion.div>

                  {/* PROOF POINTS */}
                  <motion.div variants={itemVariants} className="border-t border-gray-100 pt-6">
                    <p className="text-sm text-gray-500 leading-relaxed text-center lg:text-left">
                      Zero commission &middot; Deposits collected automatically &middot; SMS reminders built in
                    </p>
                  </motion.div>
                </div>
              </motion.div>

              {/* RIGHT COLUMN: Visual Container */}
              <motion.div
                className="order-1 lg:order-2"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="relative rounded-[3rem] p-4 md:p-8 lg:p-12 overflow-visible h-[480px] lg:h-[550px] flex items-center justify-center">

                  {/* Soft Gradient Background Blob - pulsing */}
                  <motion.div
                    animate={prefersReducedMotion ? {} : { scale: [1, 1.1, 1], opacity: [0.8, 0.6, 0.8] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-br from-purple-600/30 via-[#A78BFA]/20 to-transparent blur-3xl rounded-full pointer-events-none"
                  ></motion.div>
                  <div className="absolute top-1/4 left-1/4 w-[200px] lg:w-[300px] h-[200px] lg:h-[300px] bg-purple-200/40 rounded-full blur-[80px] mix-blend-multiply pointer-events-none"></div>

                  {/* Phone Mockup with Parallax */}
                  <motion.div
                    style={{ y: yHeroPhone }}
                    className="relative z-10 transform scale-100 items-center justify-center flex"
                  >
                    <img src={phoneLeft} alt="HYVE booking app showing a provider's calendar with upcoming appointments" className="w-[240px] lg:w-[320px] drop-shadow-[0_35px_60px_-15px_rgba(124,58,237,0.3)] rounded-[2.5rem] lg:rounded-[3rem] border-[6px] lg:border-[8px] border-white bg-white" />

                    {/* Floating Badge 1 */}
                    <motion.div
                      style={{ y: yHeroBadge1 }}
                      className="absolute -right-4 lg:-right-8 top-1/4 bg-white/80 backdrop-blur-md border border-white/40 p-3 lg:p-4 rounded-xl lg:rounded-2xl shadow-xl"
                    >
                      <div className="text-[10px] lg:text-xs text-gray-500 mb-0.5 lg:mb-1">Calendar</div>
                      <div className="text-xs lg:text-sm font-bold text-gray-900">Fully Booked</div>
                    </motion.div>

                    {/* Floating Badge 2 */}
                    <motion.div
                      style={{ y: yHeroBadge2 }}
                      className="absolute -left-4 lg:-left-8 bottom-[15%] bg-white/80 backdrop-blur-md border border-white/40 p-3 lg:p-4 rounded-xl lg:rounded-2xl shadow-xl"
                    >
                      <div className="flex items-center gap-2 lg:gap-3">
                        <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <MdCheck className="text-purple-600 text-[14px] lg:text-[16px]" />
                        </div>
                        <div>
                          <div className="text-[10px] lg:text-xs text-gray-500">Deposit</div>
                          <div className="text-xs lg:text-sm font-bold text-gray-900">Collected</div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="pt-12 md:pt-20 pb-16 md:pb-24 bg-gray-200 overflow-hidden relative" id="benefits">
          <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-transparent to-transparent pointer-events-none"></div>
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-20 max-w-3xl mx-auto"
            >
              <h2 className="text-[36px] md:text-[48px] tracking-tight leading-tight">
                <span className="block text-gray-600 font-normal mb-2">Built for solo beauty pros,</span>
                <span className="block font-bold text-gray-900">run your chair like a CEO.</span>
              </h2>
            </motion.div>

            <motion.div
              className="grid lg:grid-cols-3 gap-8 lg:gap-6 items-center"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <motion.div variants={itemVariants} className="lg:-mt-12">
                <Card3D className="h-full">
                  <div className="relative bg-white bg-opacity-80 backdrop-blur-sm p-8 rounded-3xl border border-white/50 shadow-lg bg-gradient-to-br from-white to-purple-50/30 h-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-50 to-white rounded-2xl shadow-inner border border-purple-100 flex items-center justify-center text-purple-600 mb-6">
                      <MdShield className="text-[30px]" />
                    </div>
                    <h3 className="text-[20px] font-bold text-gray-900 mb-3">Protect your income</h3>
                    <p className="text-gray-600 leading-relaxed text-[14px]">
                      Secure every appointment with automated deposits and strictly enforced no-show fees.
                    </p>
                  </div>
                </Card3D>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Card3D className="h-full">
                  <div className="relative bg-purple-50 p-10 rounded-[2rem] shadow-xl z-10 border border-purple-200 h-full">
                    <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-purple-600 mb-6">
                      <MdChatBubble className="text-[36px]" />
                    </div>
                    <h3 className="text-[24px] font-bold text-gray-900 mb-3">Stop juggling apps</h3>
                    <p className="text-gray-900/80 leading-relaxed font-medium">
                      Bookings, payments, and client texts all live in one app. No more switching between Instagram DMs and Venmo.
                    </p>
                    <div className="mt-6 flex gap-1.5 justify-end">
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full opacity-40"></div>
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full opacity-60"></div>
                      <div className="w-1.5 h-1.5 bg-purple-600 rounded-full opacity-80"></div>
                    </div>
                  </div>
                </Card3D>
              </motion.div>

              <motion.div variants={itemVariants} className="lg:mt-12">
                <Card3D className="h-full">
                  <div className="relative bg-white p-8 rounded-3xl border-2 border-dashed border-gray-300 shadow-lg h-full">
                    <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-700 mb-6">
                      <MdReceiptLong className="text-[30px]" />
                    </div>
                    <h3 className="text-[20px] font-bold text-gray-900 mb-3">Know what you pay</h3>
                    <p className="text-gray-600 leading-relaxed text-[14px]">
                      Transparent, flat pricing means no surprise commission fees eating into your hard-earned profits.
                    </p>
                  </div>
                </Card3D>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="pt-32 pb-16 md:py-24 bg-white relative overflow-hidden" id="features">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #7C3AED 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-6"
            >
              <div className="max-w-2xl relative">
                <h2 className="text-[36px] md:text-[48px] font-bold text-gray-900 mb-6 tracking-tight leading-tight">
                  Everything you need to <br /> run your chair like a business.
                </h2>

              </div>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-12 gap-8 auto-rows-fr"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              <motion.div variants={itemVariants} className="md:col-span-7">
                <motion.div whileHover={{ scale: 1.01 }} className="h-full bg-gray-100 rounded-[2rem] p-8 md:p-12 border border-gray-100 relative overflow-hidden group shadow-md hover:shadow-xl transition-shadow">
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-purple-600 mb-6">
                      <MdCalendarMonth className="text-[24px]" />
                    </div>
                    <h3 className="text-[24px] font-bold text-gray-900 mb-4">Smart Bookings & Calendar</h3>
                    <p className="text-gray-600 mb-8 max-w-sm">Syncs with your personal life. Set custom buffer times and manage waitlists automatically.</p>
                    <div className="space-y-3">
                      <span className="flex items-center gap-3 text-[14px] font-medium text-gray-900">
                        <span className="w-5 h-5 rounded-full bg-purple-600/20 flex items-center justify-center">
                          <MdCheck className="text-purple-600 text-[12px] font-bold" />
                        </span>
                        <span>Google Calendar Sync</span>
                      </span>
                      <span className="flex items-center gap-3 text-[14px] font-medium text-gray-900">
                        <span className="w-5 h-5 rounded-full bg-purple-600/20 flex items-center justify-center">
                          <MdCheck className="text-purple-600 text-[12px] font-bold" />
                        </span>
                        <span>Gap Optimization</span>
                      </span>
                    </div>
                  </div>
                  <div className="absolute right-[-40px] bottom-[-40px] w-64 h-48 bg-white rounded-xl shadow-lg border border-gray-100 p-4 transform rotate-[-5deg] group-hover:rotate-0 transition-transform duration-500">
                    <div className="space-y-2 opacity-50">
                      <div className="h-8 bg-purple-50 rounded w-full"></div>
                      <div className="h-8 bg-gray-50 rounded w-3/4"></div>
                      <div className="h-8 bg-gray-50 rounded w-5/6"></div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div variants={itemVariants} className="md:col-span-5">
                <motion.div whileHover={{ y: -5 }} className="h-full bg-[#0B1120] text-white rounded-[2rem] p-8 md:p-12 relative overflow-hidden flex flex-col justify-between group shadow-xl">
                  <div>
                    <h3 className="text-[24px] font-bold mb-4">Deposits & Protection</h3>
                    <p className="text-gray-400 text-[14px] leading-relaxed mb-6">
                      Collect deposits automatically at booking. Secure a card on file for every client.
                    </p>
                  </div>
                  <div className="flex items-center gap-4 mt-auto">
                    <div className="bg-white/10 p-3 rounded-full backdrop-blur-md border border-white/10 transform group-hover:scale-110 transition-transform">
                      <MdCreditCard className="text-[24px] text-green-400" />
                    </div>
                    <div>
                      <div className="text-[12px] text-gray-400 uppercase tracking-wider">Deposit Collected</div>
                      <div className="text-[18px] font-bold text-green-400">$50.00</div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div variants={itemVariants} className="md:col-span-5">
                <motion.div whileHover={{ scale: 1.02 }} className="h-full bg-purple-50/50 rounded-[2rem] p-8 md:p-12 border border-purple-600/10 flex flex-col group shadow-md hover:shadow-xl transition-shadow">
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-purple-600">
                      <MdForum className="text-[24px]" />
                    </div>
                    <span className="bg-purple-600 text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide">New</span>
                  </div>
                  <h3 className="text-[24px] font-bold text-gray-900 mb-2">Two-Way Texting</h3>
                  <p className="text-gray-600 text-[14px] mb-6">Dedicated business number. Automated reminders. Broadcast openings.</p>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-auto bg-white p-3 rounded-xl shadow-sm w-fit self-end transform -rotate-2 group-hover:rotate-0 transition-transform"
                  >
                    <div className="text-[10px] text-gray-400 mb-1">To: Client</div>
                    <div className="text-[12px] font-medium text-gray-900">Hey! I have a 2pm opening tomorrow...</div>
                  </motion.div>
                </motion.div>
              </motion.div>

              <motion.div variants={itemVariants} className="md:col-span-7">
                <motion.div whileHover={{ y: -5 }} className="h-full bg-white rounded-[2rem] p-8 md:p-12 border border-gray-200 shadow-lg hover:shadow-2xl relative group transition-shadow">
                  <div className="absolute top-6 right-6 text-gray-200">
                    <MdFolderOpen className="text-[60px]" />
                  </div>
                  <div className="relative z-10">
                    <h3 className="text-[24px] font-bold text-gray-900 mb-4">Client History & Notes</h3>
                    <p className="text-gray-600 mb-6 max-w-sm">Store formulas, preferences, and photos. Track total spend and visit frequency privately.</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-orange-50 text-orange-600 border border-orange-100 px-3 py-1 rounded-full text-[12px] font-bold transform hover:scale-110 transition-transform">Color Formula</span>
                      <span className="bg-blue-50 text-blue-600 border border-blue-100 px-3 py-1 rounded-full text-[12px] font-bold transform hover:scale-110 transition-transform">Allergies</span>
                      <span className="bg-purple-50 text-purple-600 border border-purple-100 px-3 py-1 rounded-full text-[12px] font-bold transform hover:scale-110 transition-transform">Photos</span>
                    </div>
                    <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100 w-full max-w-sm group-hover:bg-white group-hover:shadow-md transition-all">
                      <div className="flex gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-gray-200"></div>
                        <div>
                          <div className="w-24 h-3 bg-gray-200 rounded mb-1"></div>
                          <div className="w-16 h-2 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                      <div className="text-[12px] text-gray-500">"Pro tip: Prefers water lukewarm. Use 5N on roots."</div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-white relative">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="text-center mb-24"
            >
              <h2 className="text-[36px] md:text-[48px] font-bold text-gray-900 mb-4">Get set up in under 10 minutes.</h2>
              <div className="inline-flex items-center gap-2 bg-[#0B1120] text-white px-4 py-1 rounded-full text-[14px] font-mono shadow-lg">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span>09:59</span>
              </div>
            </motion.div>

            <div className="relative">
              <motion.div
                initial={{ height: 0 }}
                whileInView={{ height: '100%' }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 rounded-full"
                style={{
                  background: 'linear-gradient(to bottom, #5B21B6 0%, #7C3AED 50%, #A78BFA 100%)'
                }}
              ></motion.div>

              <div className="space-y-24 relative z-10">
                {/* Step 1 */}
                <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16 group relative">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="md:w-1/2 flex md:justify-end text-right order-2 md:order-1"
                  >
                    <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-lg w-full max-w-sm hover:shadow-2xl transition-shadow">
                      <h3 className="text-[20px] font-bold text-gray-900 mb-2">Download the app</h3>
                      <p className="text-gray-600 text-[14px] mb-4">Create your profile and list your services in minutes.</p>
                      <div className="mt-4 flex justify-end">
                        <MdQrCode2 className="text-[36px] text-gray-900" />
                      </div>
                    </div>
                  </motion.div>
                  <div className="md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 order-1 md:order-2">
                    <div className="w-14 h-14 bg-white border-2 border-purple-600 rounded-full flex items-center justify-center shadow-md z-20 relative">
                      <span className="text-[20px] font-bold text-gray-900">1</span>
                    </div>
                  </div>
                  <div className="md:w-1/2 order-3 md:order-3"></div>
                </div>

                {/* Step 2 */}
                <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16 group relative">
                  <div className="md:w-1/2 order-3 md:order-1"></div>
                  <div className="md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 order-1 md:order-2">
                    <div className="w-14 h-14 bg-white border-2 border-purple-600 rounded-full flex items-center justify-center shadow-md z-20 relative">
                      <span className="text-[20px] font-bold text-gray-900">2</span>
                    </div>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="md:w-1/2 order-2 md:order-3"
                  >
                    <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-lg w-full max-w-sm relative hover:shadow-2xl transition-shadow">
                      <div className="absolute -top-3 -left-3 bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded">TAKES 2 MIN</div>
                      <h3 className="text-[20px] font-bold text-gray-900 mb-2">Set your rules once</h3>
                      <p className="text-gray-600 text-[14px] mb-4">Define your deposit amount, cancellation policy, and hours.</p>
                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <span className="text-[12px] font-bold text-gray-500 uppercase">Deposit</span>
                        <div className="w-24 h-4 bg-gray-200 rounded-full relative">
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-purple-600 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Step 3 */}
                <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16 group relative">
                  <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="md:w-1/2 flex md:justify-end text-right order-2 md:order-1"
                  >
                    <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-lg w-full max-w-sm hover:shadow-2xl transition-shadow">
                      <h3 className="text-[20px] font-bold text-gray-900 mb-2">Share your booking link</h3>
                      <p className="text-gray-600 text-[14px] mb-4">Put your unique booking link in your Instagram bio.</p>
                      <button className="w-full bg-[#0B1120] text-white py-2 rounded-lg text-[14px] font-bold flex items-center justify-center gap-2 hover:bg-[#1E1A2E] transition-colors">
                        <MdLink className="text-[14px]" /> business-name.uhyve.com
                      </button>
                    </div>
                  </motion.div>
                  <div className="md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 order-1 md:order-2">
                    <div className="w-14 h-14 bg-white border-2 border-purple-600 rounded-full flex items-center justify-center shadow-md z-20 relative">
                      <span className="text-[20px] font-bold text-gray-900">3</span>
                    </div>
                  </div>
                  <div className="md:w-1/2 order-3 md:order-3"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gray-100 overflow-hidden" id="pricing">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 50 }}
              className="relative bg-[#0B1120] rounded-[2.5rem] overflow-hidden shadow-2xl cursor-default"
            >
              <div className="absolute inset-[2px] bg-[#0B1120] rounded-[2.4rem] z-0"></div>

              <div className="relative z-10 flex flex-col md:flex-row min-h-[500px]">
                <div className="p-10 md:p-14 md:w-3/5 flex flex-col justify-center text-center md:text-left">
                  <h2 className="text-[36px] font-bold text-white mb-4">Flat, simple pricing.</h2>
                  <p className="text-gray-400 mb-8 font-normal text-[18px]">Everything you need to grow, for less than the cost of a haircut.</p>

                  <div className="space-y-5 mb-10 w-fit mx-auto md:w-full md:mx-0">
                    <span className="flex items-center gap-4 text-white md:justify-start">
                      <div className="bg-purple-600/20 p-1.5 rounded-full">
                        <MdCheck className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium">No long-term contracts</span>
                    </span>
                    <span className="flex items-center gap-4 text-white md:justify-start">
                      <div className="bg-purple-600/20 p-1.5 rounded-full">
                        <MdCheck className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium">Cancel anytime</span>
                    </span>
                    <span className="flex items-center gap-4 text-white md:justify-start">
                      <div className="bg-purple-600/20 p-1.5 rounded-full">
                        <MdCheck className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium">1 month free trial</span>
                    </span>
                  </div>

                  <div className="flex flex-col gap-4 mb-6 md:mb-0 md:flex-row md:flex-nowrap">
                    <a
                      href="https://apps.apple.com/ca/app/uhyve/id6751551335"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-4 rounded-full font-bold transition-colors w-full md:w-auto md:min-w-[160px] flex flex-col items-center justify-center transform hover:scale-105 active:scale-95 duration-200 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                      aria-label="Download HYVE for iPhone"
                    >
                      <span className="text-[10px] font-medium uppercase tracking-wide opacity-80 mb-1">Download for</span>
                      <div className="flex items-center gap-2">
                        <MdPhoneIphone className="text-[20px]" />
                        <span className="text-[16px] font-bold">iPhone</span>
                      </div>
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.uhyve.app&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#171122] border-2 border-white/20 hover:border-white/40 hover:bg-[#1E1A2E] text-white px-6 py-4 rounded-full font-bold transition-colors w-full md:w-auto md:min-w-[160px] flex flex-col items-center justify-center transform hover:scale-105 active:scale-95 duration-200 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                      aria-label="Download HYVE for Android"
                    >
                      <span className="text-[10px] font-medium uppercase tracking-wide opacity-80 mb-1">Download for</span>
                      <div className="flex items-center gap-2">
                        <MdAndroid className="text-[20px]" />
                        <span className="text-[16px] font-bold">Android</span>
                      </div>
                    </a>
                  </div>

                  <p className="mt-6 text-[12px] text-gray-500 font-medium tracking-wide uppercase text-center md:text-left">Trusted by braiders, lash techs, and barbers</p>
                </div>

                <div className="md:w-2/5 bg-gradient-to-b from-[#1E1A2E] to-[#0B1120] p-10 md:p-14 flex flex-col justify-center items-center border-t md:border-t-0 md:border-l border-[#312447]/50 relative overflow-hidden">
                  <div className="text-[6rem] md:text-[8rem] leading-none font-black text-white tracking-tight relative z-10 mb-2" style={{ fontWeight: 900 }}>
                    <span className="text-[30px] md:text-[36px] absolute top-4 md:top-8 -left-4 md:-left-6 text-gray-400 font-normal">$</span>
                    <CountUp to={21} />
                  </div>
                  <p className="text-white text-[18px] font-medium mb-8">/ month CAD</p>
                  <div
                    className="bg-[#0B1120]/80 border border-[#312447] p-3 rounded-lg flex items-center gap-3"
                  >
                    <MdSavings className="text-green-400" />
                    <div className="text-left">
                      <div className="text-[12px] text-gray-400">Vs. Competitors</div>
                      <div className="text-[14px] font-bold text-white">Save $50/mo</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-white relative overflow-hidden">
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-[30px] font-bold text-gray-900 mb-4">Frequently asked questions.</h2>
              <a className="text-purple-600 font-medium inline-flex items-center gap-1 group" href="mailto:info@uhyve.com">
                Still have questions? Chat with us
                <MdArrowForward className="text-[14px] group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* FAQ 1 */}
              <motion.div
                layout
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="md:col-span-1 border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-600/30"
              >
                <button
                  className="flex justify-between items-center p-6 cursor-pointer bg-white w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 rounded-2xl"
                  onClick={() => toggleFaq(1)}
                  aria-expanded={openFaq === 1}
                  aria-controls="faq-answer-1"
                >
                  <span className="font-bold text-gray-900 text-[18px] transition-colors">Is there a free trial?</span>
                  <motion.div
                    animate={{ rotate: openFaq === 1 ? 45 : 0 }}
                    className="w-11 h-11 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0 ml-4"
                  >
                    <MdAdd className="text-gray-400" aria-hidden="true" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === 1 && (
                    <motion.div
                      id="faq-answer-1"
                      role="region"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <p className="text-gray-600 text-[15px] leading-relaxed">
                          Yes! You get a full 1-month free trial to test out all the features. No commitment required, and you can cancel anytime from your phone settings.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* FAQ 2 */}
              <motion.div
                layout
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="md:col-span-1 border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-600/30"
              >
                <button
                  className="flex justify-between items-center p-6 cursor-pointer bg-white w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 rounded-2xl"
                  onClick={() => toggleFaq(2)}
                  aria-expanded={openFaq === 2}
                  aria-controls="faq-answer-2"
                >
                  <span className="font-bold text-gray-900 text-[18px] transition-colors">Does it sync with my calendar?</span>
                  <motion.div
                    animate={{ rotate: openFaq === 2 ? 45 : 0 }}
                    className="w-11 h-11 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0 ml-4"
                  >
                    <MdAdd className="text-gray-400" aria-hidden="true" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === 2 && (
                    <motion.div
                      id="faq-answer-2"
                      role="region"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <p className="text-gray-600 text-[15px] leading-relaxed">
                          Absolutely. uHyve syncs directly with your Google Calendar, so your personal events automatically block off time in your booking schedule. No double bookings.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* FAQ 3 */}
              <motion.div
                layout
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="md:col-span-1 border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-600/30"
              >
                <button
                  className="flex justify-between items-center p-6 cursor-pointer bg-white w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 rounded-2xl"
                  onClick={() => toggleFaq(3)}
                  aria-expanded={openFaq === 3}
                  aria-controls="faq-answer-3"
                >
                  <span className="font-bold text-gray-900 text-[18px] transition-colors">How do deposits work?</span>
                  <motion.div
                    animate={{ rotate: openFaq === 3 ? 45 : 0 }}
                    className="w-11 h-11 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0 ml-4"
                  >
                    <MdAdd className="text-gray-400" aria-hidden="true" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === 3 && (
                    <motion.div
                      id="faq-answer-3"
                      role="region"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <p className="text-gray-600 text-[15px] leading-relaxed">
                          You set the deposit amount. When clients book, they are charged the deposit instantly. The funds go directly to your connected Stripe account and are deposited into your bank account.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* FAQ 4 */}
              <motion.div
                layout
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="md:col-span-1 border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300 hover:border-purple-600/30"
              >
                <button
                  className="flex justify-between items-center p-6 cursor-pointer bg-white w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 rounded-2xl"
                  onClick={() => toggleFaq(4)}
                  aria-expanded={openFaq === 4}
                  aria-controls="faq-answer-4"
                >
                  <span className="font-bold text-gray-900 text-[18px] transition-colors">Can I import my client list?</span>
                  <motion.div
                    animate={{ rotate: openFaq === 4 ? 45 : 0 }}
                    className="w-11 h-11 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0 ml-4"
                  >
                    <MdAdd className="text-gray-400" aria-hidden="true" />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openFaq === 4 && (
                    <motion.div
                      id="faq-answer-4"
                      role="region"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <p className="text-gray-600 text-[15px] leading-relaxed">
                          Yes, you can easily import your contacts directly from your phone. Send them a blast text with your new booking link in one click.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}